import { ApolloError, Reference } from '@apollo/client';
import {
  Category,
  CategoryCreateMutation,
  CategoryFieldsFragmentDoc,
  CategoryTranslationInputInput,
  LanguageId,
  useCategoryCreateMutation,
} from 'lib-knowledge-management-common/graphql';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { ampli } from 'lib-shared-tracking';

type MutationFunction = ReturnType<typeof useCategoryCreateMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type CreateCategoryFn = (data: {
  categoryTitle: string;
  parentRef: ParentRef;
  languageId: LanguageId;
}) => MutationReturnValue;
type UseCategoryCreateReturnValue = [
  CreateCategoryFn,
  ReturnType<typeof useCategoryCreateMutation>[1],
];

type UseCategoryCreateParams = {
  onCompleted: (data: CategoryCreateMutation) => void;
  onError: (error: ApolloError) => void;
};

export function useCategoryCreate({
  onCompleted,
  onError,
}: UseCategoryCreateParams): UseCategoryCreateReturnValue {
  const [createMutation, mutationResult] = useCategoryCreateMutation({
    onCompleted,
    onError,
  });
  const onCreateCategory: CreateCategoryFn = ({
    categoryTitle,
    parentRef,
    languageId,
  }) => {
    const translations: CategoryTranslationInputInput[] = [
      {
        languageId,
        title: categoryTitle,
      },
    ];
    return createMutation({
      variables: {
        translations,
        parentId: parentRef.id,
      },
      update(cache, { data }) {
        if (!data) return null;

        cache.modify<Category>({
          id: cache.identify(parentRef),
          fields: {
            categories(existingCategoryRefs = [], { readField }) {
              const newCategory = data.categoryCreate;
              if (!newCategory) throw new Error('No category returned');

              const newCategoryRef = cache.writeFragment({
                data: newCategory,
                fragment: CategoryFieldsFragmentDoc,
              });

              // Quick safety check - if the new comment is already
              // present in the cache, we don't need to add it again.
              if (
                existingCategoryRefs.some(
                  (ref) => readField('id', ref) === newCategory.id,
                )
              ) {
                return existingCategoryRefs;
              }
              return [...existingCategoryRefs, newCategoryRef] as Reference[];
            },
          },
        });
      },
    }).then((args) => {
      ampli.knowledgeCategoryCreated({
        categoryId: args.data?.categoryCreate?.id || 'unknown',
      });
      return args;
    });
  };

  return [onCreateCategory, mutationResult];
}
